import { ADS_MANAGEMENT_READ_TIMEOUT_MS, apiClient } from './apiClient';

/**
 * Operator management surfaces (ops-coverage #4, #5, #6, #7, #8).
 *
 * ⛔ EVERY READ HERE CARRIES read_ok, AND IT IS NOT OPTIONAL DECORATION.
 * The backend returns `read_ok: false` with an empty list when it could not
 * reach Google, precisely so the UI can tell "nothing is wrong" apart from "we
 * could not look". Rendering an empty array as "all clear" is how a client was
 * told they had zero spend in a month they spent $3,953. Any component using
 * these types must branch on read_ok BEFORE it branches on length.
 */

export interface ReadState {
  read_ok: boolean;
  message: string;
}

// --- #4 change history ------------------------------------------------------

export interface ChangeRow {
  change_date_time: string;
  user_email: string;
  client_type: string;
  resource_type: string;
  operation: string;
  changed_fields: string[];
  resource_name: string;
  campaign: string;
  ad_group: string;
  is_budget_change: boolean;
  /** Daily budget either side of the change, in dollars. Null when Google did
   *  not populate that side or the row is not a budget change. */
  budget_before: number | null;
  budget_after: number | null;
  budget_delta: number | null;
  /** What changed, in words: "Daily budget $50 → $75 (+$25)". A field name
   *  like `amount_micros` is not a change. */
  summary: string;
  /** Blank user_email or a SUBSCRIPTION client_type means Google did it. */
  made_by_a_person: boolean;
}

export interface ChangeHistoryResponse extends ReadState {
  tenant_id: string;
  customer_id: string;
  /** Google caps this at 30 days and it cannot be backfilled. */
  window_days: number;
  changes: ChangeRow[];
  budget_changes: ChangeRow[];
  /** The row cap was hit, so every count here is a floor, not a total. */
  truncated: boolean;
}

export async function getChangeHistory(
  tenantId: string,
  days = 30,
): Promise<ChangeHistoryResponse> {
  const { data } = await apiClient.get<ChangeHistoryResponse>(
    '/google-ads/change-history',
    { params: { tenant_id: tenantId, days }, timeout: ADS_MANAGEMENT_READ_TIMEOUT_MS },
  );
  return data;
}

// --- #5 conversion view -----------------------------------------------------

export interface ConversionActionRow {
  id: string;
  name: string;
  status: string;
  type: string;
  category: string;
  origin: string;
  /** `customers/{cid}/conversionActions/{id}` — the key operator overrides
   *  below are saved and looked up by. */
  resource_name: string;
  primary_for_goal: boolean;
  conversions_30d: number;
  all_conversions_30d: number;
  /** Enabled, used for bidding, and recording nothing. */
  is_silent: boolean;
  /** A Google Business Profile action counted as primary. It DOES record
   *  conversions — they just are not leads, which is the point. */
  is_gbp_local: boolean;
  concern: string;
  /** Operator's category correction. Takes DISPLAY precedence over
   *  `category` when set, but `category` is never overwritten — both are
   *  present so a diff ("overridden from: Purchase") stays possible. */
  category_override: string | null;
  notes: string | null;
  tier: string | null;
}

export interface ConversionViewResponse extends ReadState {
  tenant_id: string;
  customer_id: string;
  /** The window Google was ACTUALLY queried over, after clamping. Print this,
   *  never the number that was requested. */
  window_days: number;
  actions: ConversionActionRow[];
  silent_count: number;
  /** GBP actions marked primary. The finding worth surfacing. */
  gbp_primary_count: number;
  /** Share of primaries that are silent, 0-1. Rank by this × spend. */
  silent_share: number;
  /** ⚠ What the server would ACCEPT right now. Read it through `canWrite()`. */
  capabilities?: GaWriteCapabilities;
}

export async function getConversionView(
  tenantId: string,
  days = 30,
): Promise<ConversionViewResponse> {
  const { data } = await apiClient.get<ConversionViewResponse>(
    '/google-ads/conversion-view',
    { params: { tenant_id: tenantId, days }, timeout: ADS_MANAGEMENT_READ_TIMEOUT_MS },
  );
  return data;
}

export interface ConversionActionOverrideResponse {
  tenant_id: string;
  conversion_action_resource_name: string;
  category_override: string | null;
  notes: string | null;
  tier: string | null;
}

/** NOT a Google Ads write — an internal annotation stored against our own
 *  Postgres, not the Ads API. `resourceName` goes as a query param (not a
 *  path segment) because it contains slashes. */
export async function updateConversionActionOverride(
  tenantId: string,
  resourceName: string,
  overrides: { category_override?: string | null; notes?: string | null; tier?: string | null },
): Promise<ConversionActionOverrideResponse> {
  const { data } = await apiClient.post<ConversionActionOverrideResponse>(
    '/google-ads/conversion-view/override',
    overrides,
    { params: { tenant_id: tenantId, resource_name: resourceName } },
  );
  return data;
}

// --- #6 keyword pause / enable ---------------------------------------------

export interface KeywordStatusResponse {
  tenant_id: string;
  status: 'ENABLED' | 'PAUSED';
  requested: number;
  /** Writes that SUCCEEDED. Never render `requested` as if it were this. */
  changed: number;
  /** Exactly the ads that changed — what Undo reverses. Deriving this from
   *  `requested` minus failures would re-enable ads that were already paused
   *  before the operator did anything. */
  changed_resource_names: string[];
  /** Refused because they changed elsewhere since we last looked. */
  drifted: string[];
  failed: string[];
  skipped: string[];
  capped: boolean;
  message: string;
}

export async function setKeywordStatus(
  tenantId: string,
  resourceNames: string[],
  status: 'ENABLED' | 'PAUSED',
): Promise<KeywordStatusResponse> {
  const { data } = await apiClient.post<KeywordStatusResponse>(
    '/google-ads/keyword-control/status',
    { tenant_id: tenantId, resource_names: resourceNames, status },
  );
  return data;
}

// --- ad group pause / enable (Management Phase 1) --------------------------

export interface AdGroupStatusResponse {
  tenant_id: string;
  status: 'ENABLED' | 'PAUSED';
  requested: number;
  /** Writes that SUCCEEDED. Never render `requested` as if it were this. */
  changed: number;
  failed: string[];
  skipped: string[];
  capped: boolean;
  message: string;
}

export async function setAdGroupStatus(
  tenantId: string,
  resourceNames: string[],
  status: 'ENABLED' | 'PAUSED',
): Promise<AdGroupStatusResponse> {
  const { data } = await apiClient.post<AdGroupStatusResponse>(
    '/google-ads/ad-group-control/status',
    { tenant_id: tenantId, resource_names: resourceNames, status },
  );
  return data;
}

// --- #7 ad monitoring + edit ------------------------------------------------

export interface MonitoredAd {
  /** adGroupAd resource name — what pause/enable mutates. Without it the
   *  action buttons would render and do nothing. */
  resource_name: string;
  ad_id: string;
  ad_name: string;
  status: string;
  approval_status: string;
  ad_strength: string;
  ad_group_id: string;
  ad_group_name: string;
  campaign_id: string;
  campaign_name: string;
  impressions: number;
  clicks: number;
  conversions: number;
  /** GBP / store visits live here. Waste is both this and conversions at 0. */
  all_conversions?: number;
  cost: number;
  /** Cost per conversion. The number that says whether the spend bought
   *  anything — Ad Strength does not. */
  cost_per_conversion: number;
  /** First RSA headline. */
  headline: string;
  final_url: string;
  ad_type: string;
  /** Backend-resolved identity for the row. Never an ad id. */
  label: string;
  /** Spent real money over 30 days and converted nothing. */
  is_waste: boolean;
  concern: string;
}

/** Write capabilities the server reports on the read that draws the control.
 *
 *  ⛔ ONE OBJECT, NOT A BOOL PER CONTROL. The first version carried
 *  `status_writes_enabled` on one response and `keyword_writes_enabled` on
 *  another. Keeping the answer on the read that draws the button is right, but
 *  a field per control does not scale — write #7 would mean a seventh field and
 *  a seventh branch. Collapsed while there were only two (Viktor). */
export interface GaWriteCapabilities {
  /** POST /google-ads/ad-control/status — pause/enable an ad. */
  ad_status_write?: boolean;
  /** POST /google-ads/keyword-control/status — pause/enable a keyword. */
  keyword_write?: boolean;
  /** POST /google-ads/ad-group-control/status — pause/enable an ad group. */
  ad_group_write?: boolean;
  /** PATCH /google-ads/campaigns/live/{status,ad-status}. Despite the name it
   *  is the live-campaign write flag, so it also answers for pausing or
   *  enabling a whole campaign (both routes share blocking_campaign_write_flag). */
  campaign_ad_status_write?: boolean;
  /** POST /google-ads/location-targeting/{add,remove} — location targeting. */
  location_targeting_write?: boolean;
  /** POST /google-ads/ad-schedule-control/{add,remove} — ad schedule. */
  ad_schedule_write?: boolean;
  /** POST /google-ads/conversion-view/override — annotate a conversion action. */
  conversion_override_write?: boolean;
}

/** Whether to draw a write control, from what the server said it would accept.
 *
 *  ⚠ THREE STATES, NOT TWO. `false` is policy — the flag is off, stay quiet.
 *  `undefined` means the object never arrived: an older backend, or a
 *  serialization bug of the kind that has silently dropped six fields in a
 *  week. Both hide the control, because absence is never permission — but the
 *  second warns, so a screen that loses its buttons to a BUG cannot look
 *  identical to one obeying a flag.
 */
export function canWrite(
  capabilities: GaWriteCapabilities | undefined,
  key: keyof GaWriteCapabilities,
): boolean {
  if (capabilities === undefined || capabilities === null) {
    console.warn(
      `[google-ads] No capabilities object on the response; hiding "${key}". ` +
        'This is a missing field, NOT a disabled feature — the server did not ' +
        'say no, it said nothing.',
    );
    return false;
  }
  return capabilities[key] === true;
}

export interface AdMonitorResponse extends ReadState {
  tenant_id: string;
  customer_id: string;
  /** The window Google was ACTUALLY queried over, after clamping. */
  window_days: number;
  ads: MonitoredAd[];
  disapproved_count: number;
  /** Ads that spent and bought nothing, with the dollars behind them. */
  waste_count: number;
  waste_spend: number;
  /** Ad groups where every enabled ad is disapproved: live keywords, no ads. */
  dark_ad_groups: string[];
  /** ⛔ What this screen CANNOT see. Ad health reads ad_group_ad; Performance
   *  Max serves from asset groups, so PMax is structurally absent. These name
   *  the exposure rather than leaving it unlabelled. */
  uncovered_pmax_campaigns: number;
  uncovered_pmax_spend: number;
  /** ⚠ What the server would ACCEPT right now, derived from the same functions
   *  that gate the writes. Read it through `canWrite()` — never index it
   *  directly, or the missing-vs-false distinction is lost. */
  capabilities?: GaWriteCapabilities;
}

export async function getAdMonitor(
  tenantId: string,
  days = 30,
): Promise<AdMonitorResponse> {
  const { data } = await apiClient.get<AdMonitorResponse>('/google-ads/ad-control', {
    params: { tenant_id: tenantId, days },
    timeout: ADS_MANAGEMENT_READ_TIMEOUT_MS,
  });
  return data;
}

export interface PolicyCheck {
  id: string;
  label: string;
  status: string;
  severity: string;
  evidence: string;
}

export interface ReplaceAdResponse {
  tenant_id: string;
  replaced: boolean;
  status: string;
  replaced_ad: string;
  warning: string;
  policy_checks: PolicyCheck[];
}

/**
 * ⛔ acknowledgeHistoryLoss IS REQUIRED AND THE SERVER REFUSES WITHOUT IT.
 * Google cannot edit an ad in place: this deletes a proven ad and starts a new
 * one with no impressions, no conversions and no Ad Strength learning. The
 * caller must have shown the operator the warning text first.
 */
export async function replaceAd(params: {
  tenantId: string;
  adGroupResourceName: string;
  existingAdResourceName: string;
  headlines: string[];
  descriptions: string[];
  finalUrl: string;
  path1?: string;
  path2?: string;
  acknowledgeHistoryLoss: boolean;
}): Promise<ReplaceAdResponse> {
  const { data } = await apiClient.post<ReplaceAdResponse>(
    '/google-ads/ad-control/replace',
    {
      tenant_id: params.tenantId,
      ad_group_resource_name: params.adGroupResourceName,
      existing_ad_resource_name: params.existingAdResourceName,
      headlines: params.headlines,
      descriptions: params.descriptions,
      final_url: params.finalUrl,
      path1: params.path1 ?? '',
      path2: params.path2 ?? '',
      acknowledge_history_loss: params.acknowledgeHistoryLoss,
    },
  );
  return data;
}

// --- #2 shared negative lists ----------------------------------------------

export interface NegativeListSummary {
  resource_name: string;
  id: string;
  name: string;
  status: string;
  keyword_count: number;
  /** How many campaigns use it. Check before editing a shared list. */
  campaign_count: number;
}

export interface NegativeListsResponse extends ReadState {
  tenant_id: string;
  customer_id: string;
  lists: NegativeListSummary[];
}

export async function getNegativeLists(
  tenantId: string,
): Promise<NegativeListsResponse> {
  const { data } = await apiClient.get<NegativeListsResponse>(
    '/google-ads/negative-lists',
    { params: { tenant_id: tenantId } },
  );
  return data;
}

/**
 * ⚠ ONE ACCOUNT PER CALL, deliberately. Attaching across several clients is a
 * fan-out of these with a per-account confirmation, so a mistake stops at one
 * client. Do not add a bulk variant here without revisiting that decision.
 */
export async function attachNegativeList(
  tenantId: string,
  sharedSet: string,
): Promise<{ newly_attached: number; message: string }> {
  const { data } = await apiClient.post('/google-ads/negative-lists/attach', {
    tenant_id: tenantId,
    shared_set: sharedSet,
  });
  return data;
}

// --- #7 ad pause / enable (the everyday action) -----------------------------

export interface AdStatusResponse {
  tenant_id: string;
  status: 'ENABLED' | 'PAUSED';
  requested: number;
  /** Writes that SUCCEEDED. Never render `requested` as if it were this. */
  changed: number;
  /** Exactly the ads that changed — what Undo reverses. Deriving this from
   *  `requested` minus failures would re-enable ads that were already paused
   *  before the operator touched anything. */
  changed_resource_names: string[];
  /** Refused because Google's live status no longer matched what we expected —
   *  somebody moved the ad in between. Not a failure: a deliberate refusal. */
  drifted: string[];
  failed: string[];
  skipped: string[];
  capped: boolean;
  message: string;
}

/**
 * Pause or enable ads.
 *
 * ⚠ Reversible, so it does not require the acknowledgement `replaceAd` does.
 * Pausing an ad stops it serving; replacing one destroys its history. Keeping
 * them as separate calls behind separate flags is what lets the safe action
 * ship without the dangerous one.
 */
export async function setAdStatus(
  tenantId: string,
  resourceNames: string[],
  status: 'ENABLED' | 'PAUSED',
  /** ⛔ UNDO ONLY. What we believe these ads are in right now. The server
   *  re-reads Google and refuses any ad that moved since, so an undo cannot
   *  silently revert somebody's edit made in the Google Ads UI in between. */
  expectedCurrentStatus?: 'ENABLED' | 'PAUSED',
): Promise<AdStatusResponse> {
  const { data } = await apiClient.post<AdStatusResponse>(
    '/google-ads/ad-control/status',
    {
      tenant_id: tenantId,
      resource_names: resourceNames,
      status,
      expected_current_status: expectedCurrentStatus ?? '',
    },
  );
  return data;
}

// --- PMax channel decomposition ---------------------------------------------

export interface PmaxChannelRow {
  channel: string;
  /** Plain-language name. The API's own values are shouty constants. */
  label: string;
  cost: number;
  conversions: number;
  impressions: number;
  clicks: number;
  /** Share of this account's PMax spend, 0-100. */
  spend_share: number;
  cost_per_conversion: number;
}

export interface PmaxChannelResponse extends ReadState {
  tenant_id: string;
  customer_id: string;
  window_days: number;
  channels: PmaxChannelRow[];
  campaign_count: number;
  total_spend: number;
}

/**
 * Where Performance Max actually spent.
 *
 * ⛔ PMax reports ONE blended cost-per-conversion across Search, Display,
 * YouTube, Discover and Maps — products that are not alike. Two client
 * accounts measured the day this shipped: beisplumbing 82% Display,
 * allstarsanitation 91% Search, indistinguishable in every other view Core has.
 */
export async function getPmaxChannels(
  tenantId: string,
  days = 30,
): Promise<PmaxChannelResponse> {
  const { data } = await apiClient.get<PmaxChannelResponse>(
    '/google-ads/pmax/channels',
    { params: { tenant_id: tenantId, days } },
  );
  return data;
}

// --- account structure: ad groups, keywords, locations, ad schedule ---------
//
// ⛔ SEARCH AND PMAX ONLY, per Art 2026-08-21. Ad groups and keywords exist
// only on Search; PMax has neither, so those tables say so rather than letting
// an empty result imply the account has none.

export interface AdGroupRow {
  resource_name: string;
  ad_group_id: string;
  name: string;
  status: string;
  campaign_name: string;
  channel_type: string;
  impressions: number;
  clicks: number;
  cost: number;
  conversions: number;
  all_conversions?: number;
  cost_per_conversion: number;
  is_waste: boolean;
}

export interface KeywordRow {
  /** What pause/enable mutates. Without it the buttons are decoration. */
  resource_name: string;
  criterion_id: string;
  text: string;
  match_type: string;
  status: string;
  ad_group_name: string;
  campaign_name: string;
  impressions: number;
  clicks: number;
  cost: number;
  conversions: number;
  all_conversions?: number;
  cost_per_conversion: number;
  is_waste: boolean;
  /** 1-10, or null when Google has not scored this keyword yet. */
  quality_score: number | null;
  /** BELOW_AVERAGE / AVERAGE / ABOVE_AVERAGE, or "" when unscored. */
  expected_ctr: string;
  landing_page_experience: string;
  ad_relevance: string;
  /** conversions / clicks. Null when clicks is 0 — a keyword with no clicks
   *  has no conversion rate, not a 0% one. */
  cvr: number | null;
}

export interface TargetingRow {
  campaign_name: string;
  /** What add_locations/add_ad_schedule attaches a new sibling criterion
   *  beside. */
  campaign_resource_name: string;
  /** ⛔ What remove_locations/remove_ad_schedule mutates. Without it the
   *  Remove control is decoration. */
  resource_name: string;
  criterion_type: string;
  label: string;
  /** Excluded targets look identical to included ones without this. */
  is_negative: boolean;
  bid_modifier: number;
}

// --- extensions (Google calls these "assets": sitelinks, callouts, etc.) ---

export interface ExtensionRow {
  /** The link resource (customer_asset or campaign_asset) — not the asset
   *  itself. Read-only section, so nothing mutates this, but it is what a
   *  future write path would need. */
  resource_name: string;
  /** Human label: "Sitelink", "Callout", "Structured snippet", "Call",
   *  "Promotion", "Lead form", "Price". */
  extension_type: string;
  /** The extension's own display text — sitelink link text + description,
   *  callout text, structured snippet header + values, phone number, etc. */
  content: string;
  /** Campaign name, or "Account-wide" for a customer-level extension. */
  scope: string;
  /** Google's own serving verdict (primary_status), not the advertiser-set
   *  status — same convention as Ad groups and Keywords above. */
  status: string;
}

// --- location targeting + ad schedule ADD/REMOVE (Management Phase 3) ------
//
// ⛔ campaign_criterion HAS NO IN-PLACE EDIT for LOCATION/AD_SCHEDULE. There
// is no editLocation()/editAdSchedule() here on purpose — changing a target
// is remove the old one + add the new one, exactly what these functions do.

/** Same contract shape as googleAdsWeeklyService's blast-radius breaker —
 *  see BlastRadiusConfirmationDetail there for the full write-up. Duplicated
 *  here (rather than imported) because this service otherwise has no
 *  dependency on the weekly module. */
export interface BlastRadiusAssessment {
  requires_confirmation: boolean;
  reasons: string[];
  summary: Record<string, unknown>;
}

export interface BlastRadiusConfirmationDetail {
  code: 'blast_radius_confirmation_required';
  assessment: BlastRadiusAssessment;
  confirmation_phrase: string;
}

export interface FailedWriteItem {
  label: string;
  reason: string;
}

export interface LocationWriteResponse {
  tenant_id: string;
  requested: number;
  changed: number;
  failed: FailedWriteItem[];
  capped: boolean;
  skipped: string[];
  message: string;
}

export async function addLocations(
  tenantId: string,
  items: { campaignResourceName: string; geoTargetConstant: string; negative?: boolean }[],
): Promise<LocationWriteResponse> {
  const { data } = await apiClient.post<LocationWriteResponse>(
    '/google-ads/location-targeting/add',
    {
      tenant_id: tenantId,
      items: items.map((it) => ({
        campaign_resource_name: it.campaignResourceName,
        geo_target_constant: it.geoTargetConstant,
        negative: it.negative ?? false,
      })),
    },
  );
  return data;
}

export async function removeLocations(
  tenantId: string,
  resourceNames: string[],
  confirmationText?: string,
): Promise<LocationWriteResponse> {
  const { data } = await apiClient.post<LocationWriteResponse>(
    '/google-ads/location-targeting/remove',
    {
      tenant_id: tenantId,
      resource_names: resourceNames,
      confirmation_text: confirmationText,
    },
  );
  return data;
}

export type AdScheduleWriteResponse = LocationWriteResponse;

export async function addAdScheduleSlots(
  tenantId: string,
  items: {
    campaignResourceName: string;
    dayOfWeek: string;
    startHour: number;
    endHour: number;
  }[],
): Promise<AdScheduleWriteResponse> {
  const { data } = await apiClient.post<AdScheduleWriteResponse>(
    '/google-ads/ad-schedule-control/add',
    {
      tenant_id: tenantId,
      items: items.map((it) => ({
        campaign_resource_name: it.campaignResourceName,
        day_of_week: it.dayOfWeek,
        start_hour: it.startHour,
        end_hour: it.endHour,
      })),
    },
  );
  return data;
}

export async function removeAdScheduleSlots(
  tenantId: string,
  resourceNames: string[],
  confirmationText?: string,
): Promise<AdScheduleWriteResponse> {
  const { data } = await apiClient.post<AdScheduleWriteResponse>(
    '/google-ads/ad-schedule-control/remove',
    {
      tenant_id: tenantId,
      resource_names: resourceNames,
      confirmation_text: confirmationText,
    },
  );
  return data;
}

export interface StructureResponse extends ReadState {
  tenant_id: string;
  customer_id: string;
  window_days: number;
  ad_groups: AdGroupRow[];
  keywords: KeywordRow[];
  locations: TargetingRow[];
  ad_schedules: TargetingRow[];
  extensions: ExtensionRow[];
  /** ⚠ What the server would ACCEPT right now. Read it through `canWrite()`. */
  capabilities?: GaWriteCapabilities;
}

export async function getStructure(
  tenantId: string,
  days = 30,
): Promise<StructureResponse> {
  const { data } = await apiClient.get<StructureResponse>(
    '/google-ads/structure',
    { params: { tenant_id: tenantId, days }, timeout: ADS_MANAGEMENT_READ_TIMEOUT_MS },
  );
  return data;
}

// --- Strategy Easy: live Google recommendations ---------------------------

export interface StrategyGoogleRec {
  resource_name: string;
  type: string;
  title: string;
  why: string;
  campaign: string;
  campaign_name?: string;
  keyword_text?: string;
  match_type?: string;
  core_offer: boolean;
}

export interface StrategyGoogleRecsResponse extends ReadState {
  tenant_id: string;
  customer_id: string;
  items: StrategyGoogleRec[];
  total: number;
  truncated: boolean;
  apply_enabled: boolean;
}

export interface StrategyGoogleRecActionResponse {
  tenant_id: string;
  resource_name: string;
  status: string;
  audit_id: string;
}

export async function listStrategyGoogleRecs(
  tenantId: string,
): Promise<StrategyGoogleRecsResponse> {
  const { data } = await apiClient.get<StrategyGoogleRecsResponse>(
    '/google-ads/strategy-recs',
    { params: { tenant_id: tenantId } },
  );
  return data;
}

export async function dismissStrategyGoogleRec(
  tenantId: string,
  resourceName: string,
): Promise<StrategyGoogleRecActionResponse> {
  const { data } = await apiClient.post<StrategyGoogleRecActionResponse>(
    '/google-ads/strategy-recs/dismiss',
    { tenant_id: tenantId, resource_name: resourceName },
  );
  return data;
}
